/**
 * Auth-Store — gleiche Logik wie im Cloud-Dashboard.
 * Storage-Key separat, damit die beiden Anwendungen nebeneinander laufen können,
 * aber das Backend kann das gleiche JWT validieren.
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

const STORAGE_KEY = 'shruby.dbadmin.auth.v1';

interface PersistedAuth {
  token: string;
  user: string;
  roles: string[];
}

function loadFromStorage(): PersistedAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedAuth;
  } catch {
    return null;
  }
}

export const useAuthStore = defineStore('auth', () => {
  const initial = loadFromStorage();
  const token = ref<string | null>(initial?.token ?? null);
  const user = ref<string | null>(initial?.user ?? null);
  const roles = ref<string[]>(initial?.roles ?? []);

  const isAuthenticated = computed(() => token.value !== null);
  const isAdmin = computed(() => roles.value.includes('ADMIN'));

  function setSession(t: string, u: string, r: string[]) {
    token.value = t;
    user.value = u;
    roles.value = r;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ token: t, user: u, roles: r }));
  }

  async function logout() {
    const hadToken = token.value !== null;
    if (hadToken) {
      try {
        const { authApi } = await import('@/api');
        await authApi.logout();
      } catch {
        // Lokal aufräumen trotzdem.
      }
    }
    token.value = null;
    user.value = null;
    roles.value = [];
    localStorage.removeItem(STORAGE_KEY);
  }

  function handleUnauthorized() {
    logout();
  }

  return {
    token,
    user,
    roles,
    isAuthenticated,
    isAdmin,
    setSession,
    logout,
    handleUnauthorized,
  };
});
